import { useCallback, useEffect, useRef } from "react"

export function useAutorepeat(
  callback,
  initialDelay,
  repeatDelay,
  isValid = true,
  doLinearRepeat = true,
  minimumRepeatTime = 50
) {
  const savedCallback = useRef() // save the callback, so we can change this without affecting the delay
  const savedIsValid = useRef()
  const initialDelayID = useRef() // id for the initial timeout
  const repeatDelayID = useRef() // is for the repeating interval

  const repeatDelayTime = useRef()

  // Save the callback
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Save whether the repeat action is still valid or not
  useEffect(() => {
    savedIsValid.current = isValid
  }, [isValid])

  // Function to simplify cancelling the timers
  // Using a callback, since this is using refs and therefore only needs to be defined once when we first use this hook
  // This is also sent back to the user as a way to stop the repeats
  const cancelTimers = useCallback(() => {
    if (initialDelayID.current) clearTimeout(initialDelayID.current)
    if (repeatDelayID.current) clearInterval(repeatDelayID.current)
  }, [])

  // Handle the actual repeating
  // This only gets redefined if the delay times or delay options gets changed
  const startRepeat = useCallback(() => {
    function tick() {
      savedCallback.current()
    }

    cancelTimers()

    // drop out if this is no longer active
    if (!savedIsValid.current) {
      return
    }

    tick()

    repeatDelayTime.current = repeatDelay

    // Handle a non linear delay
    const nonLinearDelay = () => {
      if (!savedIsValid.current) {
        cancelTimers()
        return
      }

      tick()

      repeatDelayTime.current =
        repeatDelayTime.current > minimumRepeatTime
          ? repeatDelayTime.current * 0.95
          : repeatDelayTime.current

      initialDelayID.current = setTimeout(
        nonLinearDelay,
        repeatDelayTime.current
      )
    }

    // handle a liner delay
    const linearDelay = () => {
      if (!savedIsValid.current) {
        cancelTimers()
        return
      }

      tick()

      repeatDelayID.current = setInterval(() => {
        if (!savedIsValid.current) {
          cancelTimers()
          return
        }

        tick()
      }, repeatDelay)
    }

    initialDelayID.current = setTimeout(
      doLinearRepeat ? linearDelay : nonLinearDelay,
      initialDelay
    )
  }, [
    cancelTimers,
    initialDelay,
    doLinearRepeat,
    minimumRepeatTime,
    repeatDelay
  ])

  // Clean up the timers if the component is removed without cancelTimers being called
  useEffect(() => {
    // Literally just handle cancelling the timers
    return () => cancelTimers()
  }, [cancelTimers])

  // Using a tuple so that the user can easily
  return [startRepeat, cancelTimers]
}

export default useAutorepeat
